import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Users, Building2, Handshake, Megaphone, HelpCircle, Send, ArrowRight, ArrowLeft, CheckCircle2, Home, Sparkles, Mail, User, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import api from '../services/api';
import AnimatedBackground from '../components/AnimatedBackground';

const categories = [
  { id: 'participant', label: 'Participant', icon: Users, color: 'from-sky-500 to-blue-600', desc: 'Inscription, evenement, dossard...', emoji: '🏃' },
  { id: 'organizer', label: 'Organisateur', icon: Building2, color: 'from-brand to-orange-600', desc: 'Tableau de bord, creation d\'evenement...', emoji: '📋' },
  { id: 'partner', label: 'Partenaire', icon: Handshake, color: 'from-emerald-500 to-teal-600', desc: 'Prestation, devis, catalogue...', emoji: '🤝' },
  { id: 'sponsor', label: 'Sponsor', icon: Megaphone, color: 'from-violet-500 to-purple-600', desc: 'Sponsoring, visibilite, partenariat...', emoji: '📣' },
  { id: 'other', label: 'Autre', icon: HelpCircle, color: 'from-slate-500 to-slate-700', desc: 'Presse, question generale, suggestion...', emoji: '💡' },
];

const ContactPage = () => {
  const [step, setStep] = useState(0); // 0=category, 1=form, 2=success
  const [category, setCategory] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [hoveredCat, setHoveredCat] = useState(null);

  const selectedCat = categories.find(c => c.id === category);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    setSending(true);
    try {
      await api.post('/contact', {
        category: category,
        category_label: selectedCat?.label,
        name: form.name,
        email: form.email,
        subject: form.subject,
        message: form.message,
      });
      setStep(2);
      toast.success('Message envoye avec succes !');
    } catch (err) {
      toast.error('Erreur lors de l\'envoi. Reessayez.');
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setStep(0);
    setCategory(null);
    setForm({ name: '', email: '', subject: '', message: '' });
  };

  return (
    <AnimatedBackground>
      {/* Mini nav */}
      <div className="fixed top-0 left-0 right-0 z-50 px-6 py-3 flex items-center justify-between">
        <Link to="/">
          <img src="/logo-light.png" alt="iBookSport" className="h-8" />
        </Link>
        <Link to="/" className="flex items-center gap-1.5 text-white/70 hover:text-white text-sm font-heading font-bold uppercase tracking-wider transition-colors" data-testid="contact-home-link">
          <Home className="w-4 h-4" /> Accueil
        </Link>
      </div>

      <div className="min-h-screen flex items-center justify-center p-4 pt-20" data-testid="contact-page">
        <div className="w-full max-w-2xl">

          {/* Header */}
          <motion.div
            className="text-center mb-8"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <motion.div
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-1.5 mb-4"
              whileHover={{ scale: 1.05 }}
            >
              <Sparkles className="w-4 h-4 text-brand" />
              <span className="font-heading font-bold uppercase tracking-widest text-xs text-white">Contactez-nous</span>
            </motion.div>
            <h1 className="font-heading text-3xl md:text-4xl font-extrabold uppercase tracking-tight text-white mb-2">
              {step === 0 && 'Vous etes...'}
              {step === 1 && `${selectedCat?.emoji} ${selectedCat?.label}`}
              {step === 2 && 'Message envoye !'}
            </h1>
            {step === 0 && (
              <p className="text-white/60 text-sm">Selectionnez votre profil pour nous envoyer un message adapte</p>
            )}
          </motion.div>

          {/* Step indicator */}
          {step < 2 && (
            <div className="flex justify-center gap-2 mb-8">
              {[0, 1].map(s => (
                <motion.div
                  key={s}
                  className={`h-1.5 rounded-full transition-all duration-500 ${s === step ? 'w-10 bg-brand' : s < step ? 'w-6 bg-brand/50' : 'w-6 bg-white/20'}`}
                  layoutId={`step-${s}`}
                />
              ))}
            </div>
          )}

          <AnimatePresence mode="wait">
            {/* Step 0: Category Selection */}
            {step === 0 && (
              <motion.div
                key="categories"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="grid grid-cols-1 sm:grid-cols-2 gap-3"
              >
                {categories.map((cat, idx) => {
                  const Icon = cat.icon;
                  const isHovered = hoveredCat === cat.id;
                  return (
                    <motion.button
                      key={cat.id}
                      className="relative group text-left overflow-hidden"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.06 }}
                      onClick={() => { setCategory(cat.id); setStep(1); }}
                      onMouseEnter={() => setHoveredCat(cat.id)}
                      onMouseLeave={() => setHoveredCat(null)}
                      whileTap={{ scale: 0.97 }}
                      data-testid={`contact-cat-${cat.id}`}
                    >
                      <div className={`relative bg-white/[0.06] backdrop-blur-sm border border-white/[0.1] p-5 transition-all duration-300 ${isHovered ? 'border-white/30 bg-white/[0.12]' : ''}`}>
                        {/* Gradient accent on hover */}
                        <motion.div
                          className={`absolute inset-0 bg-gradient-to-br ${cat.color} opacity-0 transition-opacity duration-300`}
                          style={{ opacity: isHovered ? 0.15 : 0 }}
                        />
                        <div className="relative flex items-center gap-4">
                          <div className={`w-12 h-12 bg-gradient-to-br ${cat.color} flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${isHovered ? 'scale-110 rotate-3' : ''}`}>
                            <Icon className="w-6 h-6 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-heading font-bold uppercase tracking-wider text-sm text-white">{cat.label}</p>
                            <p className="text-white/50 text-xs mt-0.5">{cat.desc}</p>
                          </div>
                          <ArrowRight className={`w-4 h-4 text-white/30 transition-all duration-300 ${isHovered ? 'text-white translate-x-1' : ''}`} />
                        </div>
                      </div>
                    </motion.button>
                  );
                })}

                {/* Last card spans full width on larger screens */}
                <div className="sm:col-span-2 text-center pt-2">
                  <p className="text-white/30 text-xs">Temps de reponse moyen : 24h en jours ouvres</p>
                </div>
              </motion.div>
            )}

            {/* Step 1: Contact Form */}
            {step === 1 && (
              <motion.form
                key="form"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                onSubmit={handleSubmit}
                className="bg-white/[0.06] backdrop-blur-sm border border-white/[0.1] overflow-hidden"
              >
                {/* Category header bar */}
                <div className={`bg-gradient-to-r ${selectedCat?.color} px-6 py-3 flex items-center justify-between`}>
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{selectedCat?.emoji}</span>
                    <span className="font-heading font-bold uppercase tracking-wider text-sm text-white">{selectedCat?.label}</span>
                  </div>
                  <button type="button" onClick={() => setStep(0)} className="text-white/70 hover:text-white text-xs font-heading uppercase tracking-wider flex items-center gap-1">
                    <ArrowLeft className="w-3 h-3" /> Changer
                  </button>
                </div>

                <div className="p-6 space-y-5">
                  {/* Name + Email row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="flex items-center gap-1.5 text-white/60 text-xs font-heading uppercase tracking-wider mb-2">
                        <User className="w-3 h-3" /> Nom complet *
                      </label>
                      <Input
                        value={form.name}
                        onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                        placeholder="Votre nom"
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/30 focus:border-brand"
                        required
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <label className="flex items-center gap-1.5 text-white/60 text-xs font-heading uppercase tracking-wider mb-2">
                        <Mail className="w-3 h-3" /> Email *
                      </label>
                      <Input
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                        placeholder="votre@email.com"
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/30 focus:border-brand"
                        required
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  {/* Subject */}
                  <div>
                    <label className="flex items-center gap-1.5 text-white/60 text-xs font-heading uppercase tracking-wider mb-2">
                      <Sparkles className="w-3 h-3" /> Objet
                    </label>
                    <Input
                      value={form.subject}
                      onChange={(e) => setForm(f => ({ ...f, subject: e.target.value }))}
                      placeholder="En quelques mots..."
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/30 focus:border-brand"
                      data-testid="contact-subject"
                    />
                  </div>

                  {/* Message */}
                  <div>
                    <label className="flex items-center gap-1.5 text-white/60 text-xs font-heading uppercase tracking-wider mb-2">
                      <MessageSquare className="w-3 h-3" /> Votre message *
                    </label>
                    <Textarea
                      value={form.message}
                      onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))}
                      placeholder="Decrivez votre demande en detail..."
                      rows={5}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/30 focus:border-brand resize-none"
                      required
                      data-testid="contact-message"
                    />
                  </div>

                  {/* Submit */}
                  <div className="flex items-center justify-between pt-2">
                    <p className="text-white/30 text-xs">* Champs obligatoires</p>
                    <Button
                      type="submit"
                      className="btn-primary gap-2"
                      disabled={sending}
                      data-testid="contact-submit"
                    >
                      {sending ? (
                        <motion.div
                          className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                        />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                      {sending ? 'Envoi en cours...' : 'Envoyer le message'}
                    </Button>
                  </div>
                </div>
              </motion.form>
            )}

            {/* Step 2: Success */}
            {step === 2 && (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center"
              >
                <motion.div
                  className="w-24 h-24 bg-emerald-500/20 border-2 border-emerald-500/40 rounded-full flex items-center justify-center mx-auto mb-6"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 200, delay: 0.1 }}
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    <CheckCircle2 className="w-12 h-12 text-emerald-400" />
                  </motion.div>
                </motion.div>

                <motion.h2
                  className="font-heading text-2xl font-extrabold uppercase text-white mb-3"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  Merci {form.name.split(' ')[0]} !
                </motion.h2>
                <motion.p
                  className="text-white/60 text-sm mb-2 max-w-md mx-auto"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  Votre message a bien ete transmis a l'equipe iBookSport.
                </motion.p>
                <motion.p
                  className="text-white/40 text-xs mb-8"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                >
                  Nous reviendrons vers vous sous 24h en jours ouvres.
                </motion.p>

                <motion.div
                  className="flex flex-col sm:flex-row gap-3 justify-center"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                >
                  <Button variant="outline" className="border-white/30 text-white hover:bg-white hover:text-asphalt gap-2" onClick={reset} data-testid="contact-new-message">
                    <Send className="w-4 h-4" /> Nouveau message
                  </Button>
                  <Link to="/">
                    <Button className="btn-primary gap-2">
                      <Home className="w-4 h-4" /> Retour a l'accueil
                    </Button>
                  </Link>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </AnimatedBackground>
  );
};

export default ContactPage;
